import defaults from 'lodash/defaults.js';
import pick from 'lodash/pick.js';
import urlJoin from 'url-join';
export function endpointWithPath(path) {
    return (serviceConfig) => {
        const serviceEndpoint = serviceConfig.options?.endpoint;
        if (!serviceEndpoint) {
            throw new Error('Unexpected error: service endpoint not found');
        }
        return urlJoin(serviceEndpoint, path);
    };
}
/**
 * Process a `ServiceConfig` and return a valid `ServiceHealthCheck` merging
 * config and health check params.
 *
 * @param defaultParams An optional, default health check config to use.
 */
export function getHealthCheck(defaultParams) {
    return (serviceConfig) => {
        // We're only guaranteed a partial ServiceHealthCheck here, but will return a complete type
        const params = defaults({}, serviceConfig.healthCheck, defaultParams);
        const { checkName } = params;
        if (!checkName) {
            return;
        }
        const endpoint = typeof params.endpoint === 'function'
            ? params.endpoint(serviceConfig)
            : (params?.endpoint ?? serviceConfig.options?.endpoint);
        const requestHeaders = typeof params.requestHeaders === 'function'
            ? params.requestHeaders(serviceConfig)
            : (params?.requestHeaders ?? serviceConfig.options?.headers);
        const requestSearchParams = typeof params.requestSearchParams === 'function'
            ? params.requestSearchParams(serviceConfig)
            : (params?.requestSearchParams ?? serviceConfig.options?.searchParams);
        if (!endpoint) {
            return;
        }
        return {
            ...params,
            checkName,
            endpoint,
            requestHeaders,
            requestSearchParams
        };
    };
}
const healthCheckDefinitions = {
    healthCheckGraphqlIntrospection: {
        title: 'GraphQL Introspection',
        type: 'object',
        properties: {
            checkName: {
                title: 'Check Name',
                description: 'The GraphQL introspection check verifies that we can introspect your service using the provided endpoint and authentication.',
                type: 'string',
                enum: ['graphqlIntrospection'],
                default: 'graphqlIntrospection'
            },
            endpoint: {
                title: 'Endpoint',
                description: 'The endpoint to use when checking the service health.',
                type: 'string'
            },
            requestHeaders: {
                title: 'Request Headers',
                description: 'Any custom headers to set with the health check request.',
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        name: {
                            type: 'string'
                        },
                        value: {
                            type: 'string'
                        }
                    }
                }
            },
            requestTimeout: {
                title: 'Request Timeout',
                description: 'The amount of time to wait when receiving a response from the health check, in seconds.',
                type: 'number',
                minimum: 2,
                maximum: 60
            }
        },
        required: ['checkName'],
        additionalProperties: false
    },
    healthCheckPing: {
        title: 'Ping',
        type: 'object',
        properties: {
            checkName: {
                title: 'Check Name',
                description: 'The ping check is a basic test of connectivity.',
                type: 'string',
                enum: ['ping'],
                default: 'ping'
            },
            endpoint: {
                title: 'Endpoint',
                description: 'The endpoint to use when checking the service health.',
                type: 'string'
            },
            requestMethod: {
                title: 'Request Method',
                type: 'string',
                enum: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
            },
            requestHeaders: {
                title: 'Request Headers',
                description: 'Any custom headers to set with the health check request.',
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        name: {
                            type: 'string'
                        },
                        value: {
                            type: 'string'
                        }
                    }
                }
            },
            requestBody: {
                title: 'Request Body',
                description: 'A custom body to send along with the ping request.',
                type: 'string'
            },
            requestBodyFormat: {
                title: 'Request Body Format',
                description: 'A custom body to send along with the ping request.',
                type: 'string',
                enum: ['string', 'form', 'json', 'form-data']
            },
            requestTimeout: {
                title: 'Request Timeout',
                description: 'The amount of time to wait when receiving a response from the health check, in seconds.',
                type: 'number',
                minimum: 2,
                maximum: 60
            },
            healthyResponseStatusCode: {
                title: 'Healthy Response Status Code',
                description: 'The response status code that qualifies as healthy.',
                type: 'number',
                minimum: 200,
                maximum: 399
            }
        },
        required: ['checkName'],
        additionalProperties: false
    },
    healthCheckNone: {
        title: 'None',
        type: 'object',
        properties: {
            checkName: {
                title: 'Check Name',
                description: 'Do not run health checks on this service.',
                type: 'string',
                enum: ['none'],
                default: 'none'
            }
        },
        required: ['checkName'],
        additionalProperties: false
    }
};
export const healthCheckNamePaths = {
    ping: 'healthCheckPing',
    graphqlIntrospection: 'healthCheckGraphqlIntrospection',
    none: 'healthCheckNone'
};
export function getHealthCheckDefinitions(paths) {
    return pick(healthCheckDefinitions, paths);
}
export function getHealthCheckProperties(paths) {
    const oneOf = paths.map((definition) => ({
        $ref: `#/definitions/${definition}`
    }));
    const properties = {
        healthCheck: {
            title: 'Health Check',
            discriminator: {
                propertyName: 'checkName'
            },
            oneOf
        }
    };
    return properties;
}
export function addHealthCheckSchema(schema, { checkNames } = {}) {
    const paths = checkNames?.map((checkName) => healthCheckNamePaths[checkName]) ?? Object.values(healthCheckNamePaths);
    const definitions = getHealthCheckDefinitions(paths);
    const properties = getHealthCheckProperties(paths);
    return {
        ...schema,
        definitions: {
            ...schema.definitions,
            ...definitions
        },
        properties: {
            ...schema.properties,
            ...properties
        }
    };
}
const healthCheckFormProps = {
    healthCheck: {
        instructions: 'A health check which will be run periodically to ensure your service is up.',
        isCollapsible: true,
        isCollapsed: true
    }
};
export function addHealthCheckFormConfig(formConfig) {
    const { properties, order } = formConfig;
    if (properties) {
        const newOrder = Object.keys(healthCheckFormProps);
        return {
            ...formConfig,
            properties: { ...properties, ...healthCheckFormProps },
            order: order ? [...order, ...newOrder] : newOrder
        };
    }
    return formConfig;
}
export const healthCheckFormConfig = {
    healthCheckPing: {
        default: {
            properties: {
                endpoint: {
                    widget: 'singleLineText',
                    instructions: 'By default your service endpoint will be used.'
                },
                requestTimeout: {
                    widget: 'singleLineText',
                    instructions: 'Default: 60 seconds'
                },
                healthyResponseStatusCode: {
                    widget: 'number',
                    instructions: 'Default: 200'
                },
                requestMethod: {
                    widget: 'dropdown',
                    instructions: 'Default: GET'
                },
                requestHeaders: {
                    widget: 'repeater',
                    properties: {
                        name: {
                            widget: 'singleLineText'
                        },
                        value: {
                            widget: 'singleLineText'
                        }
                    },
                    order: ['name', 'value']
                },
                requestBody: {
                    widget: 'paragraphText',
                    instructions: 'Default: empty / unset'
                },
                requestBodyFormat: {
                    widget: 'dropdown',
                    instructions: 'Default: string if a body is provided'
                }
            },
            order: [
                'endpoint',
                'requestTimeout',
                'healthyResponseStatusCode',
                'requestMethod',
                'requestHeaders',
                'requestBody',
                'requestBodyFormat'
            ]
        }
    },
    healthCheckGraphqlIntrospection: {
        default: {
            properties: {
                endpoint: {
                    widget: 'singleLineText',
                    instructions: 'If you leave this blank your service endpoint will be used.'
                },
                requestTimeout: {
                    widget: 'number',
                    instructions: 'Default: 30 seconds'
                },
                requestHeaders: {
                    widget: 'repeater',
                    properties: {
                        name: {
                            widget: 'singleLineText'
                        },
                        value: {
                            widget: 'singleLineText'
                        }
                    },
                    order: ['name', 'value']
                }
            },
            order: ['endpoint', 'requestTimeout', 'requestHeaders']
        }
    }
};
function getHealthCheckUpdate(formData) {
    const healthCheckUpdate = formData.healthCheck;
    if (healthCheckUpdate?.requestHeaders) {
        healthCheckUpdate.requestHeaders = healthCheckUpdate.requestHeaders.reduce((prev, { name, value }) => Object.assign(prev, {
            [name]: value
        }), {});
    }
    return healthCheckUpdate;
}
export function updateHealthCheck(config, formData) {
    const healthCheckUpdate = getHealthCheckUpdate(formData);
    if (healthCheckUpdate !== undefined) {
        return { ...config, healthCheck: healthCheckUpdate };
    }
    return config;
}
